/**
 * <hash-target-align target="element-id">
 *
 * When a visitor arrives with #element-id in the URL (for example from another page),
 * keeps that element aligned at the top of the viewport while late content settles,
 * such as an app form that loads after the page or a web font swap. It never changes
 * the target's content. It stops after a few seconds or as soon as the visitor
 * scrolls, taps, clicks or presses a key, so it never fights the visitor.
 * The target's CSS scroll-margin-top controls the spacing above it.
 */
class HashTargetAlign extends HTMLElement {
  connectedCallback() {
    const id = this.getAttribute('target');
    if (!id || window.location.hash !== `#${id}`) return;

    const target = document.getElementById(id);
    if (!target) return;

    this.controller = new AbortController();
    const { signal } = this.controller;
    const stop = () => this.disconnectedCallback();

    for (const type of ['wheel', 'touchstart', 'mousedown', 'keydown']) {
      window.addEventListener(type, stop, { once: true, passive: true, signal });
    }

    const align = () => {
      const margin = parseFloat(getComputedStyle(target).scrollMarginTop) || 0;
      if (Math.abs(target.getBoundingClientRect().top - margin) > 4) {
        target.scrollIntoView({ block: 'start', behavior: 'instant' });
      }
    };

    window.addEventListener('load', align, { once: true, signal });

    if ('ResizeObserver' in window) {
      this.observer = new ResizeObserver(align);
      this.observer.observe(document.body);
      this.observer.observe(target);
    }

    requestAnimationFrame(align);
    this.timer = window.setTimeout(stop, 6000);
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
    window.clearTimeout(this.timer);
  }
}

if (!customElements.get('hash-target-align')) {
  customElements.define('hash-target-align', HashTargetAlign);
}
